import React, { useEffect, useMemo, useRef } from 'react';

/**
 * COMPONENTE PANTALLA GIGANTE: VAS O NO VAS (Maletines)
 */
const BriefcaseScreen = ({ currentGameState }) => {

    // ==========================================================================
    // EXTRACCIÓN DE DATOS DEL BACKEND (Con Fallbacks de Seguridad)
    // ==========================================================================
    const {
        phase = 'WAITING',
        briefcases = [],
        suspenseBriefcaseId = null,
        targetedBriefcaseId = null,
        revealData = null
    } = currentGameState || {};

    // Referencia al lienzo de confeti para dibujar
    const confettiCanvasRef = useRef(null);
    const animationFrameIdRef = useRef(null);
    const rainTimeoutRef = useRef(null);
    const confettiParticlesRef = useRef([]);

    // ==========================================================================
    // RUTAS DE ASSETS (Imágenes)
    // ==========================================================================
    const ASSETS = {
        closed: '/img/games/briefcase/cerrado.png',
        opened: '/img/games/briefcase/abierto.png',
        gift: '/img/games/briefcase/regalo.png',
        winner: '/img/games/briefcase/regalo.png'
    };

    // ==========================================================================
    // CONFIGURACIÓN DE LA CUADRÍCULA (GRID)
    // ==========================================================================
    const gridStyle = useMemo(() => {
        const count = briefcases.length || 9;
        let columns = 3;
        
        if (count <= 4) columns = 2;
        else if (count > 9 && count <= 16) columns = 4;
        else if (count > 16) columns = 5;

        const rows = Math.ceil(count / columns);

        return {
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`
        };
    }, [briefcases.length]);

    // ==========================================================================
    // LÓGICA BLINDADA PARA EL MODAL Y EL SUSPENSO
    // Si el backend omite revealData, lo deducimos del maletín activo.
    // ==========================================================================
    const activeId = suspenseBriefcaseId || targetedBriefcaseId;
    const activeBriefcase = briefcases.find(b => b.id === activeId) || {};
    
    let modalTheme = 'loser';
    let modalText = 'Sigue participando';

    if (revealData) {
        modalTheme = revealData.theme || 'loser';
        modalText = revealData.text || 'Sigue participando';
    } else if (activeBriefcase.state === 'opened') {
        // Fallback de emergencia si el backend no mandó revealData
        modalTheme = activeBriefcase.prizeTheme || 'loser';
        if (modalTheme === 'winner') modalText = '¡PREMIO MAYOR!';
        else if (modalTheme === 'prize') modalText = '¡Ganaste un premio secundario!';
    }

    const isRevealPhase = phase?.toUpperCase() === 'REVEAL_MODAL';
    const isWinningPhase = isRevealPhase && modalTheme === 'winner';

    // ==========================================================================
    // LÓGICA DEL CONFETI (Efectos de Partículas Seguros)
    // ==========================================================================
    useEffect(() => {
        const canvas = confettiCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', resizeCanvas);
        resizeCanvas();

        const createConfettiPiece = (x, y, isBurst = false) => {
            const colors = ['#ffd700', '#ffb300', '#ffffff', '#4ade80', '#60a5fa', '#ff4d4d'];
            return {
                x: x, y: y, 
                r: Math.random() * 6 + 4, 
                color: colors[Math.floor(Math.random() * colors.length)],
                tilt: Math.floor(Math.random() * 10) - 10,
                tiltAngleIncremental: Math.random() * 0.07 + 0.05,
                tiltAngle: Math.random() * Math.PI,
                vx: isBurst ? (Math.random() - 0.5) * 45 : (Math.random() - 0.5) * 2.5,
                vy: isBurst ? (Math.random() * -40) - 20 : Math.random() * 2 + 1.5,
                isBurst: isBurst
            };
        };

        const drawConfetti = () => {
            if (!canvas || !ctx) return;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            confettiParticlesRef.current.forEach((p, index) => {
                if (!p) return;
                p.tiltAngle += p.tiltAngleIncremental; 
                p.x += p.vx; 
                p.y += p.vy;

                if (p.isBurst) { 
                    p.vy += 0.85; 
                    p.vx *= 0.98; 
                } else { 
                    p.vy += 0.02; 
                    if (p.vy > 3.5) p.vy = 3.5; 
                    p.vx *= 0.99; 
                }
                p.tilt = Math.sin(p.tiltAngle) * 15;

                // Control de vida de la partícula
                if (p.y > canvas.height + 20) {
                    if (!p.isBurst && isWinningPhase) {
                        p.y = -Math.random() * 120 - 10; 
                        p.x = Math.random() * canvas.width;
                        p.vy = Math.random() * 2 + 1.5; 
                        p.vx = (Math.random() - 0.5) * 2;
                    } else { 
                        confettiParticlesRef.current[index] = null; 
                    }
                }

                if (p) {
                    ctx.beginPath(); 
                    ctx.lineWidth = p.r / 2; 
                    ctx.strokeStyle = p.color;
                    ctx.moveTo(p.x + p.tilt + p.r / 4, p.y); 
                    ctx.lineTo(p.x + p.tilt, p.y + p.tilt + p.r / 4); 
                    ctx.stroke();
                }
            });

            confettiParticlesRef.current = confettiParticlesRef.current.filter(p => p !== null);
            animationFrameIdRef.current = requestAnimationFrame(drawConfetti);
        };

        // Disparadores
        const fireSideBursts = () => {
            confettiParticlesRef.current = [];
            for(let i=0; i<100; i++) confettiParticlesRef.current.push(createConfettiPiece(0, window.innerHeight, true)); 
            for(let i=0; i<100; i++){ 
                let p = createConfettiPiece(window.innerWidth, window.innerHeight, true); 
                p.vx *= -1; 
                confettiParticlesRef.current.push(p); 
            }
        };

        const startConfettiRain = () => {
            for (let i = 0; i < 150; i++) {
                confettiParticlesRef.current.push(createConfettiPiece(Math.random() * canvas.width, -Math.random() * canvas.height, false));
            }
        };

        // Activador del Confeti
        if (isWinningPhase) {
            fireSideBursts();
            // Limpiamos timeouts previos por seguridad
            if (rainTimeoutRef.current) clearTimeout(rainTimeoutRef.current);
            rainTimeoutRef.current = setTimeout(startConfettiRain, 500);
            
            if (!animationFrameIdRef.current) drawConfetti();
        }

        // Limpieza profunda al desmontar o cambiar fase
        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (rainTimeoutRef.current) clearTimeout(rainTimeoutRef.current);
            if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
            animationFrameIdRef.current = null;
        };
    }, [isWinningPhase]); // Solo se re-evalúa cuando entramos o salimos de la fase ganadora


    // ==========================================================================
    // HELPERS VISUALES
    // ==========================================================================
    const getBriefcaseImage = (briefcase) => {
        if (briefcase.state === 'opened') {
            if (briefcase.prizeTheme === 'winner') return ASSETS.winner;
            if (briefcase.prizeTheme === 'prize') return ASSETS.gift;
            return ASSETS.opened;
        }
        return ASSETS.closed; 
    };

    const getModalImage = (theme) => {
        if (theme === 'winner') return ASSETS.winner;
        if (theme === 'prize') return ASSETS.gift;
        return ASSETS.opened;
    };

    return (
        <div className="briefcase-screen">
            <div className="briefcase-screen__overlay-bg"></div>
            <img src="/img/page/xolos-five.png" className="LogoADSLxZtudia" alt="Logo" style={{ width: '100%', maxWidth: '300px', marginTop: '10px', zIndex: '201', position: 'fixed', top: '0', left: '50%', transform: 'translateX(-50%)' }} />

            {/* CONTENIDO PRINCIPAL: LA CUADRÍCULA */}
            <div className={`briefcase-screen__content ${phase?.toUpperCase() === 'SUSPENSE' ? 'briefcase-screen__content--blurred' : ''}`}>
                <div className="briefcase-screen__grid" style={gridStyle}>
                    {briefcases.map((bc, index) => (
                        <div 
                            key={bc.id} 
                            className={`briefcase-screen__item 
                                ${bc.state === 'closed' ? 'briefcase-screen__item--active' : ''}
                                ${bc.state === 'selected' ? 'briefcase-screen__item--selected' : ''}
                                ${bc.state === 'opened' ? 'briefcase-screen__item--opened' : ''}
                            `}
                            style={{ 
                                '--jitter-duration': `${(2.5 + (index % 3)).toFixed(2)}s`,
                                '--jitter-delay': `${((index * 0.3) % 2).toFixed(2)}s`
                            }}
                        >
                            <div 
                                className="briefcase-screen__image" 
                                style={{ backgroundImage: `url(${getBriefcaseImage(bc)})` }}
                            >
                                <span className="briefcase-screen__number">{bc.id}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* OVERLAY DE SUSPENSO */}
            {phase?.toUpperCase() === 'SUSPENSE' && (
                <div className="briefcase-screen__suspense">
                    <div className="briefcase-screen__suspense-briefcase">
                        <div 
                            className="briefcase-screen__image" 
                            style={{ backgroundImage: `url(${ASSETS.closed})` }}
                        >
                            {/* Usamos activeId como seguro si suspenseBriefcaseId es nulo */}
                            <span className="briefcase-screen__suspense-number">{activeId}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE RESULTADO BLINDADO: Ya no depende estrictamente de revealData */}
            {isRevealPhase && (
                <div className="briefcase-screen__modal">
                    <div className="briefcase-screen__modal-box-wrapper" style={{ width: '100%', maxWidth: '42rem', position: 'relative' }}>
                        
                        <div className={`briefcase-screen__modal-box briefcase-screen__modal-box--${modalTheme?.toLowerCase()}`}>
                            <div className="briefcase-screen__modal-image-container" style={{ backgroundImage: `url(${getModalImage(modalTheme?.toLowerCase())})` }}></div>
                            
                            <div className="briefcase-screen__modal-text">
                                {modalText}
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* LIENZO PARA EL CONFETI */}
            <canvas ref={confettiCanvasRef} className="briefcase-screen__confetti" style={{ pointerEvents: 'none' }}></canvas>
            
            <p className='FooterCredits' style={{ position: 'fixed', bottom: '0', left: '50%', transform: 'translateX(-50%)', zIndex: '201' }}>Hecho con ♥ por Five Digital Minds | 2026</p>
        </div>
    );
};

export { BriefcaseScreen };